import * as React from 'react';
import { observer } from 'mobx-react';
import { PagePaymentSuccessViewModel } from './page-payment-success-view-model';
import { GoogleAnalyticsWrapper } from '../../infrastructure/google-analytics-wrapper';
import { WebGivingUserAction } from '../../webgiving-user-actions';
import { Button, ButtonStyle } from '../../primitives/button/button';
import { ButtonLink } from '../../primitives/button/button-link';
import { RightChevronLink } from '../../primitives/link/link';
import { ErrorMessage } from '../../components/error-message/error-message';
import { FadeTransition, fadeTransitionDuration } from '../../components/fade-transition';
import { PanelContentTransition } from '../../components/panel-content-transition';
import { FocusOnMount } from '../../components/focus-on-mount-hoc';
import { AppStoreCallToAction } from '../../components/app-store-call-to-action/app-store-call-to-action';
import { getPageClasses } from '../helpers/get-page-classes';
import { GiftComplete } from '../../components/gift/gift-complete';
import { PanelHeading } from '../../components/panel-heading';
import { injectWebGivingProps, InjectableWebGivingProps, raiseAction } from '../../utils/injectable-props';
import { PanelFooter } from '../../components/panel-footer';
import { pageFooter } from '../../main/webgiving-main.less';
import { HeartAnimation } from '../../components/heart-animation/heart-animation';
import * as styles from './page-payment-success.less';
import { frequencyLabelLowerCase, frequencyLabelSentenceCase } from '../../utils/recurring-utils';
import { Formatter } from '../../../Shared/utils/formatter';
import { FrequencyCode } from '../../webgiving-generated';
import { Text } from '../../components/text';
import { Switch } from '../../components/switch';
import { StandardTooltip, TooltipPlacement } from '../../components/tooltip/tooltip';
export { PagePaymentSuccessViewModel };

@injectWebGivingProps
@observer
export class PagePaymentSuccess extends React.Component<{
	vm: PagePaymentSuccessViewModel,
	transitionState: string,
} & InjectableWebGivingProps> {
	panelBodyNode: HTMLDivElement;

	render() {
		const { vm, transitionState } = this.props;
		const {
			pageShouldBeVisible,
			error,
			data: {
				successThankYouMessage,
				getAppUrl,
				paymentLabel,
				merchantAppName,
				isRecurring,
				stillProcessing,
				wasDelayed,
				giftAmount,
				fundName,
				emailAddress,
				receiptSent,
				merchantCurrency,
				merchantName,
				recurringStartDate,
				recurringFrequency,
				hasCustomApp,
				hasWelcomeSmsBeenSent,
				hideAppCallToAction,
				backToAppLink,
				isBranded,
				recurringInfo,
				appContentCard,
				isOneTimeFutureGift,
				oneTimeGiftDate,
				fastModeAvailable,
				fastModeDailyLimit,
				thankYouText,
				returnButtonText,
				returnButtonUrl
			},
			isFastModeOn,
			isFastModeLoading
		} = vm;

		const elementsShouldEnter = transitionState === 'entered';
		const shouldShowCallToAction = !hideAppCallToAction && hasCustomApp;
		const hasUiConfigReturnOverride = NewFeatures.SunsetFastpay && !!returnButtonUrl && !!returnButtonText;

		return (
			<div className={getPageClasses(pageShouldBeVisible, false)} data-pp-at-target={pageShouldBeVisible ? 'Payment Success page' : null}>
				<PanelHeading vm={vm}>
					<FadeTransition in={elementsShouldEnter}>
						<div>
							<ErrorMessage error={error} />
							<HeartAnimation />
							<GiftComplete
								emailAddress={emailAddress}
								fundName={fundName}
								giftAmount={giftAmount}
								isRecurring={isRecurring}
								receiptSent={receiptSent}
								merchantCurrency={merchantCurrency}
								merchantName={merchantName}
								paymentLabel={paymentLabel}
								stillProcessing={stillProcessing}
								wasDelayed={wasDelayed}
								startDate={recurringStartDate}
								frequency={recurringFrequency}
								displayRecurringSuggestion={!!recurringInfo && recurringInfo.ShowSuggestion}
								isOneTimeFutureGift={isOneTimeFutureGift}
								oneTimeGiftDate={oneTimeGiftDate}
								thankYouText={thankYouText}
							/>
							{NewFeatures.WebGiving_FastMode && fastModeAvailable && (
								<div className={styles.fastModeWrap}>
									<div className={styles.fastModeToggleWrap}>
										<h2 className={styles.fastModeTitle} data-pp-at-target="fast-mode-enable-title">
											<Text>ENABLE FASTMODE</Text>
										</h2>
										<StandardTooltip
											placement={TooltipPlacement.Top}
											content={<Text>{`By enabling Fastmode, you'll be able to give up [to double of your average/highest ${paymentLabel.NounLowerCase} from the last 90 days] without a confirmation code. To receive a confirmation code each time, turn Fastmode off.`}</Text>}
											tooltipContentId="fast-mode-enable-tooltip"
										/>
										<Switch
											className={styles.fastModeSwitch}
											disabled={isFastModeLoading}
											value={isFastModeOn}
											data-pp-at-target="fast-mode-enable-switch"
											onChangeHandler={this.updateFastMode}
										/>
									</div>
									{!!fastModeDailyLimit && <p className={styles.fastModelimit}><Text>Fastmode daily limit is </Text><strong>{merchantCurrency} {`$${Formatter.formatNumberForDisplay(fastModeDailyLimit, 2)}`}</strong></p>}
								</div>
							)}
							<p>
								<FocusOnMount delay={fadeTransitionDuration}>
									{hasUiConfigReturnOverride ? (
										<ButtonLink
											href={returnButtonUrl}
											className="button"
											buttonStyle={ButtonStyle.Ghost}
											acceptanceTestTargetId="return button"
										>
											<Text>{returnButtonText}</Text>
										</ButtonLink>
									) : (
										this.giveAgainOrRecurring(vm)
									)}
								</FocusOnMount>
							</p>
							{
								NewFeatures.GoodbyeFastpay && backToAppLink &&
								<p>
									<RightChevronLink
										href={backToAppLink}
										branded={isBranded}
										acceptanceTestTargetId="back to app"
										contentProps={{ className: styles.backToApp }}><Text>Back to app</Text></RightChevronLink>
								</p>
							}
						</div>
					</FadeTransition>
				</PanelHeading>
				{(successThankYouMessage) && (<div className="panel-body" ref={this.savePanelBodyNode}>
					<PanelContentTransition in={elementsShouldEnter} setContentHeight={this.setContentHeight} className="panel-body-content">
						<div>
							<div className={styles.customSuccessThankYouMessage} dangerouslySetInnerHTML={{ __html: successThankYouMessage as string }}></div>
						</div>
					</PanelContentTransition>
				</div>)}
				{shouldShowCallToAction &&
					<PanelFooter className={pageFooter}>
						<AppStoreCallToAction getAppUrl={getAppUrl}
							mobileAppName={merchantAppName}
							paymentLabel={paymentLabel}
							hasCustomApp={hasCustomApp}
							hasWelcomeSmsBeenSent={hasWelcomeSmsBeenSent}
							appContentCard={appContentCard}
						/>
					</PanelFooter>}
			</div>
		);
	}

	setContentHeight = (offsetHeight: number) => {
		if (this.panelBodyNode) {
			this.panelBodyNode.style.minHeight = `${offsetHeight}px`;
		}
	}

	private giveAgainOrRecurring = (vm: PagePaymentSuccessViewModel) => {
		const {
			isLoading,
			data: {
				recurringInfo,
				paymentLabel,
			},
		} = vm;
		return (recurringInfo && recurringInfo.Frequency && frequencyLabelSentenceCase(recurringInfo.Frequency) && recurringInfo.ShowSuggestion
			? <Button
				className="button"
				onClick={this.setRecurring}
				buttonStyle={ButtonStyle.Ghost}
				acceptanceTestTargetId="Schedule recurring"
				isLoading={isLoading}
			>
				<Text>{paymentLabel.VerbSentenceCase}</Text>{` $${Formatter.formatNumberForDisplay(recurringInfo.Amount, 2)}`}
				{recurringInfo.Frequency === FrequencyCode.FirstAndFifteenth ? <Text> on </Text> : ' '}<Text>{frequencyLabelLowerCase(recurringInfo.Frequency)}</Text>
			</Button>
			: <Button
				className="button"
				onClick={this.giveAgain}
				buttonStyle={ButtonStyle.Ghost}
				acceptanceTestTargetId="Give again"
				isLoading={isLoading}
			>
				<Text>{`${paymentLabel.VerbSentenceCase} again`}</Text>
			</Button>
		);
	}


	private giveAgain = () => {
		GoogleAnalyticsWrapper.sendEvent('WebGiving Page View', 'giveAgain');
		raiseAction(new WebGivingUserAction.GiveAgain(), this.props);

		this.clearPreconfiguredUrl();
		this.handleNext();
	}

	private savePanelBodyNode = (panelBodyNode: HTMLDivElement) => this.panelBodyNode = panelBodyNode;

	private setRecurring = () => {
		this.clearPreconfiguredUrl();
		GoogleAnalyticsWrapper.sendEvent('WebGiving Page View', 'setRecurringSuggestion');
		raiseAction(new WebGivingUserAction.SetRecurringSuggestion(), this.props);

		this.handleNext();
	}

	private clearPreconfiguredUrl = () => {
		const query = window.location.search.substring(1);
		if (query.length) {
			if (window.history && window.history.pushState) {
				window.history.pushState({}, document.title, window.location.pathname);
			}
		}
	}

	private updateFastMode = (mode: boolean) => {
		this.props.vm.setFastModeLoading(true);
		raiseAction(new WebGivingUserAction.UpdateFastModeEnabled(mode), this.props);
	}

	private handleNext = () => {
		raiseAction(new WebGivingUserAction.SubmitCurrentPage(), this.props);
	}
}
